import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import BSButton from 'Components/Button/BSButton';
import Styles from './BulkDeleteConfirmModal.module.scss';

const BulkDeleteConfirmModal = ({
  show,
  onHide,
  onConfirm,
  selectedCount = 0,
  isLoading = false,
  itemType = 'items',
  showKeyboardShortcuts = true,
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const isConfirmationValid = confirmationText.toUpperCase() === 'DELETE';

  // Reset confirmation text when modal opens/closes
  useEffect(() => {
    if (!show) {
      setConfirmationText('');
    }
  }, [show]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!show || isLoading) return;

      // Enter key to confirm delete (only if confirmation is valid)
      if (event.key === 'Enter' && isConfirmationValid) {
        event.preventDefault();
        onConfirm();
      }
      // Escape key to cancel
      if (event.key === 'Escape') {
        event.preventDefault();
        onHide();
      }
    };

    if (showKeyboardShortcuts) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [show, onConfirm, onHide, isLoading, showKeyboardShortcuts, isConfirmationValid]);
  const getItemText = () => {
    return selectedCount === 1 ? itemType.slice(0, -1) : itemType;
  };

  const getButtonText = () => {
    if (isLoading) {
      return (
        <>
          <span className="spinner-border spinner-border-sm me-2" />
          Deleting...
        </>
      );
    }
    return (
      <span
        style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
      >
        <FiTrash2 size={16} className="me-2" />
        Delete Briefs
      </span>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop={isLoading ? 'static' : true}
      keyboard={!isLoading}
    >
      <Modal.Header closeButton={!isLoading} className={Styles.ModalHeader}>
        <Modal.Title className={Styles.ModalTitle}>
          <FiAlertTriangle size={24} className={Styles.WarningIcon} />
          Confirm Bulk Delete
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={Styles.ModalBody}>
        <div className={Styles.WarningSection}>
          <p className={Styles.WarningText}>
            Are you sure you want to delete {selectedCount} {getItemText()}?
          </p>
          <p className={Styles.DescriptionText}>
            This action cannot be undone. All selected {itemType} will be
            permanently deleted.
          </p>
        </div>

        <div className={Styles.ConfirmationSection}>
          <label className={Styles.ConfirmationLabel}>
            Type <strong>DELETE</strong> to confirm
          </label>
          <input
            type="text"
            className={Styles.ConfirmationInput}
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder="Type DELETE here"
            disabled={isLoading}
            autoFocus
          />
          {confirmationText && !isConfirmationValid && (
            <small className={Styles.ValidationError}>
              Please type DELETE exactly to confirm
            </small>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className={Styles.ModalFooter}>
        <BSButton
          variant="light"
          ButtonText="Cancel"
          onClick={onHide}
          disabled={isLoading}
          classList="btn button white_btn"
          aria-label="Cancel deletion"
          style={{
            width: '140px',
            height: '40px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 16px',
            flexShrink: 0,
            minWidth: '140px',
            maxWidth: '140px',
          }}
        />
        <BSButton
          variant="danger"
          ButtonText={getButtonText()}
          onClick={onConfirm}
          disabled={isLoading || !isConfirmationValid}
          classList="btn button danger_btn"
          aria-label="Confirm deletion of briefs"
          style={{
            fontSize: '14px',
            lineHeight: '24px',
            fontWeight: '500',
            padding: '8px 16px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '140px',
            flexShrink: 0,
          }}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default BulkDeleteConfirmModal;
